import { Tuner } from '@tunasong/icons'
import type { ListItemProps, TypographyProps } from '@tunasong/ui-lib'
import { Dialog, Typography, ListItemButton, ListItemIcon, ListItemText, useDimensions } from '@tunasong/ui-lib'
import type { MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import AudioPermissions from './audio-permissions.js'
import { useDefaultInput } from './default-input.js'
import { TunerView } from './dsp/index.js'
import styles from './tuner-menu.module.css'

export interface TunerMenuItemProps extends Omit<ListItemProps, 'button'> {
  onClick?: () => void
  onClose?: () => void
  variant?: TypographyProps['variant']
}

export const useTunerMenuItem = (props: TunerMenuItemProps = {}) => {
  const { onClose, onClick, variant } = props

  /** A window on desktop and tablet, full screen on a phone, as the mixer (GH #2999) */
  const { isTiny } = useDimensions()
  const [showTuner, setShowTuner] = useState(false)

  const input = useDefaultInput()

  useEffect(() => {
    if (!input) {
      return
    }
    input.enableDSPFeature('tuner', showTuner)

    // eslint-disable-next-line react-compiler/react-compiler
    input.monitor = showTuner
  }, [input, showTuner])

  const handleShowTuner = (ev: MouseEvent) => {
    ev.preventDefault()
    setShowTuner(true)
    if (onClick) {
      onClick()
    }
  }

  const handleHideTuner = () => {
    setShowTuner(false)
    if (onClose) {
      onClose()
    }
  }

  return {
    dialog: (
      <Dialog fullScreen={isTiny} open={showTuner} onClose={handleHideTuner} title="Tuner" fullWidth maxWidth="xs">
        <AudioPermissions />
        {input ? <TunerView audioController={input} showInput={true} /> : null}
      </Dialog>
    ),
    menuItem: (
      <ListItemButton title="Tuner" onClick={handleShowTuner}>
        <ListItemIcon className={styles.listItemIcon}>
          <Tuner />
        </ListItemIcon>
        <ListItemText>
          <Typography variant={variant}>Tuner</Typography>
        </ListItemText>
      </ListItemButton>
    ),
  }
}
